import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { useGetActivityFeedQuery, useGetAvailableFeedQuery } from 'store/services/activity/activityApi';
import { useActivitySocket } from './useActivitySocket';
import { Activity, ActivityType } from 'types/interfaces/activity';
import { ALL_ACTIVITY_TYPES, DEFAULT_ACTIVE_TYPES, FILTER_DISPLAY_ORDER } from 'screens/Main/tabs/Activity/constants';

const FEED_PAGE_SIZE = 30;
const SCROLL_THRESHOLD = 200; // px from top before considered "scrolled down"
const FILTER_STORAGE_KEY = 'activity-filter-types';

/** Load saved filter types from localStorage, falling back to defaults */
const getInitialActiveTypes = (): Set<ActivityType> => {
  try {
    const saved = localStorage.getItem(FILTER_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as ActivityType[];
      if (Array.isArray(parsed) && parsed.length > 0) {
        const valid = parsed.filter(t => FILTER_DISPLAY_ORDER.includes(t));
        if (valid.length > 0) return new Set(valid);
      }
    }
  } catch { /* ignore corrupt data */ }
  return new Set(DEFAULT_ACTIVE_TYPES);
};

/** Transform an AvailableFeedItem into the unified Activity shape.
 *  Handles both the dedicated AvailableFeedItem shape and pre-formatted Activity objects. */
const toActivity = (item: any): Activity => {
  // If the backend already returns Activity-shaped objects, pass through
  if (item.type === 'available' && item.id && item.card && item.user && item.createdAt) {
    return {
      ...item,
      // Ensure wantEntryId is present
      wantEntryId: item.wantEntryId ?? undefined,
      // Ensure user has points field
      user: {
        ...item.user,
        points: item.user.points ?? null,
        countryCode: item.user.countryCode ?? item.user.country ?? null,
      },
      // Ensure card has collectorNumber
      card: {
        ...item.card,
        collectorNumber: item.card.collectorNumber ?? null,
      },
    };
  }

  // AvailableFeedItem shape → Activity
  return {
    id: `available_${item.wantEntryId}_0`,
    type: 'available' as const,
    createdAt: item.timestamp ?? item.createdAt,
    user: {
      id: item.user?.id,
      username: item.user?.username ?? '',
      avatarUrl: item.user?.avatarUrl ?? null,
      countryCode: item.user?.countryCode ?? item.user?.country ?? null,
      points: item.user?.points ?? null,
      isElite: item.user?.isElite,
      showAnimatedBorder: item.user?.showAnimatedBorder,
      borderColor1: item.user?.borderColor1,
      borderColor2: item.user?.borderColor2,
    },
    card: {
      id: item.card?.id,
      name: item.card?.name ?? '',
      scryfallId: item.card?.scryfallId ?? '',
      imageUrl: item.card?.imageUrl ?? '',
      setCode: item.card?.setCode ?? '',
      collectorNumber: item.card?.collectorNumber ?? null,
      foil: item.card?.foil ?? false,
    },
    pointValue: item.card?.pointValue ?? item.pointValue ?? null,
    wantEntryId: item.wantEntryId,
  };
};

interface UseActivityFeedOptions {
  enableSocket?: boolean;
  pollingInterval?: number;
}

interface UseActivityFeedReturn {
  /** Visible activities (filtered, respecting pause state) */
  activities: Activity[];
  /** Whether the initial data is loading */
  isLoading: boolean;
  /** Whether more pages are available */
  hasMore: boolean;
  /** Load next page of historical activity */
  loadMore: () => void;
  /** Whether the feed is paused */
  isPaused: boolean;
  /** Toggle pause/resume */
  togglePause: () => void;
  /** Number of pending items while paused */
  pendingCount: number;
  /** Currently active filter types */
  activeTypes: Set<ActivityType>;
  /** Toggle a specific activity type filter */
  toggleType: (type: ActivityType) => void;
  /** Whether the WebSocket is connected */
  isSocketConnected: boolean;
  /** IDs of items that should animate on entry */
  newItemIds: Set<string>;
  /** Mark an item as having finished its entry animation */
  clearNewItem: (id: string) => void;
  /** Whether we're loading more pages */
  isFetchingMore: boolean;
  /** Number of items buffered while scrolled down */
  scrolledPendingCount: number;
  /** Flush scroll-buffered items into the feed with animation */
  showBuffered: () => void;
  /** Scroll event handler — bind to the scroll container */
  onScroll: (e: React.UIEvent<HTMLElement>) => void;
}

export const useActivityFeed = (
  options: UseActivityFeedOptions = {}
): UseActivityFeedReturn => {
  const {
    enableSocket = true,
    pollingInterval = 60000,
  } = options;

  // Filter state — loaded from localStorage or defaults to Available + Wanted
  const [activeTypes, setActiveTypes] = useState<Set<ActivityType>>(getInitialActiveTypes);

  // Persist filter selection to localStorage
  useEffect(() => {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(Array.from(activeTypes)));
  }, [activeTypes]);

  // Pause state
  const [isPaused, setIsPaused] = useState(false);
  const bufferedIdsRef = useRef<Set<string>>(new Set());
  const [pendingCount, setPendingCount] = useState(0);

  // Scroll-aware buffering state
  const isScrolledDownRef = useRef(false);
  const scrollBufferedIdsRef = useRef<Set<string>>(new Set());
  const [scrolledPendingCount, setScrolledPendingCount] = useState(0);

  // Track new items for entry animation
  const [newItemIds, setNewItemIds] = useState<Set<string>>(new Set());

  // Pagination cursor for load more
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  // Socket connection state
  const isSocketConnected = useSelector(
    (state: RootState) => state.activity.isConnected
  );

  // Handle new activity from WebSocket
  const handleActivity = useCallback(
    (activity: Activity) => {
      if (isPaused) {
        // Buffer the item — it's already in RTK cache but we track it
        bufferedIdsRef.current.add(activity.id);
        setPendingCount(bufferedIdsRef.current.size);
      } else if (isScrolledDownRef.current) {
        // User is scrolled down — buffer for the pill
        scrollBufferedIdsRef.current.add(activity.id);
        setScrolledPendingCount(scrollBufferedIdsRef.current.size);
      } else {
        // Mark as new for entry animation
        setNewItemIds(prev => {
          const next = new Set(prev);
          next.add(activity.id);
          return next;
        });
      }
    },
    [isPaused]
  );

  // Initialize socket
  useActivitySocket({
    enabled: enableSocket,
    onActivity: handleActivity,
  });

  // Fetch feed data
  const shouldPoll = !isSocketConnected;
  const {
    data,
    isLoading,
    isFetching,
  } = useGetActivityFeedQuery(
    { limit: FEED_PAGE_SIZE, cursor },
    {
      pollingInterval: shouldPoll ? pollingInterval : 0,
      refetchOnMountOrArgChange: true,
    }
  );

  // Available feed — only fetched when the Available pill is active
  const isAvailableActive = activeTypes.has('available');
  const { data: availableData } = useGetAvailableFeedQuery(
    { limit: 20, hours: 24 },
    { skip: !isAvailableActive, pollingInterval: 120000 }
  );

  // All activities from cache
  const allActivities = useMemo(() => data?.activities ?? [], [data]);
  const hasMore = data?.hasMore ?? false;

  // Transform available items into Activity shape
  const availableActivities = useMemo(() => {
    if (!isAvailableActive || !availableData?.items) return [];
    return availableData.items.map(toActivity);
  }, [isAvailableActive, availableData]);

  // Apply filters, pause buffering, scroll buffering, and merge available feed
  const activities = useMemo(() => {
    let filtered = allActivities;

    // Hide buffered items when paused
    if (isPaused && pendingCount > 0) {
      filtered = filtered.filter(a => !bufferedIdsRef.current.has(a.id));
    }

    // Hide scroll-buffered items when scrolled down
    if (scrolledPendingCount > 0) {
      filtered = filtered.filter(a => !scrollBufferedIdsRef.current.has(a.id));
    }

    // Apply type filter for WebSocket-driven types
    const wsActiveTypes = ALL_ACTIVITY_TYPES.filter(t => activeTypes.has(t));
    if (wsActiveTypes.length < ALL_ACTIVITY_TYPES.length) {
      filtered = filtered.filter(a => activeTypes.has(a.type));
    }

    // Merge available items and sort by timestamp
    if (isAvailableActive && availableActivities.length > 0) {
      const merged = [...availableActivities, ...filtered];
      merged.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      return merged;
    }

    return filtered;
  }, [allActivities, isPaused, activeTypes, pendingCount, scrolledPendingCount, isAvailableActive, availableActivities]);

  // Load more historical activities
  const loadMore = useCallback(() => {
    if (data?.nextCursor && !isFetching) {
      setCursor(data.nextCursor);
    }
  }, [data?.nextCursor, isFetching]);

  // Toggle pause/resume
  const togglePause = useCallback(() => {
    setIsPaused(prev => {
      if (prev) {
        // Resuming — mark all buffered items as new for animation
        const buffered = bufferedIdsRef.current;
        if (buffered.size > 0) {
          setNewItemIds(prevNew => {
            const next = new Set(prevNew);
            buffered.forEach(id => next.add(id));
            return next;
          });
        }
        bufferedIdsRef.current = new Set();
        setPendingCount(0);
      }
      return !prev;
    });
  }, []);

  // Toggle activity type filter
  const toggleType = useCallback((type: ActivityType) => {
    setActiveTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) {
        // Don't allow deselecting all types
        if (next.size > 1) {
          next.delete(type);
        }
      } else {
        next.add(type);
      }
      return next;
    });
  }, []);

  // Clear new item animation flag
  const clearNewItem = useCallback((id: string) => {
    setNewItemIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  // Scroll handler — tracks whether user is scrolled down
  const onScroll = useCallback((e: React.UIEvent<HTMLElement>) => {
    const scrollTop = e.currentTarget.scrollTop;
    const wasScrolledDown = isScrolledDownRef.current;
    isScrolledDownRef.current = scrollTop > SCROLL_THRESHOLD;

    // If user scrolled back to top, auto-flush buffered items
    if (wasScrolledDown && !isScrolledDownRef.current && scrollBufferedIdsRef.current.size > 0) {
      const buffered = scrollBufferedIdsRef.current;
      setNewItemIds(prev => {
        const next = new Set(prev);
        buffered.forEach(id => next.add(id));
        return next;
      });
      scrollBufferedIdsRef.current = new Set();
      setScrolledPendingCount(0);
    }
  }, []);

  // Flush scroll-buffered items (called from "new items" pill click)
  const showBuffered = useCallback(() => {
    const buffered = scrollBufferedIdsRef.current;
    if (buffered.size > 0) {
      setNewItemIds(prev => {
        const next = new Set(prev);
        buffered.forEach(id => next.add(id));
        return next;
      });
      scrollBufferedIdsRef.current = new Set();
      setScrolledPendingCount(0);
    }
  }, []);

  return {
    activities,
    isLoading,
    hasMore,
    loadMore,
    isPaused,
    togglePause,
    pendingCount,
    activeTypes,
    toggleType,
    isSocketConnected,
    newItemIds,
    clearNewItem,
    isFetchingMore: isFetching && !!cursor,
    scrolledPendingCount,
    showBuffered,
    onScroll,
  };
};

export default useActivityFeed;
